import fetchCollectionDisplayPromoProgress from "@faire/web-api/api/product-based-collections/productBasedCollectionToken/active-promo-progress/post";
import { IRetailerProductBasedCollectionPromoProgressRequest } from "@faire/web-api/indigofair/data/IRetailerProductBasedCollectionPromoProgressRequest";
import { IRetailerProductBasedCollectionResponse } from "@faire/web-api/indigofair/data/IRetailerProductBasedCollectionResponse";
import { moneyOrZero } from "@faire/web/common/money";
import { formatMoney } from "@faire/web/common/money";
import { MoneyMath } from "@faire/web/common/MoneyMath";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useCartItems } from "@faire/retailer/hooks/cart/useCartItems";
import { canAddToCart } from "@faire/retailer/lib/canAddToCart";

type IRetailerProductBasedCollection =
  IRetailerProductBasedCollectionResponse.IRetailerProductBasedCollection;

export const useCollectionDisplayPromo = (
  productBasedCollection?: IRetailerProductBasedCollection
) => {
  const isCollectionDisplayPromoActive = useMemo(
    () => productBasedCollection?.active_display_promo?.type === "FREE_DISPLAY",
    [productBasedCollection]
  );

  const { data: promoProgressResponse, refetch: fetchDisplayPromoProgress } =
    useQuery({
      queryKey: [
        "collectionDisplayPromoProgress",
        productBasedCollection?.token,
        productBasedCollection?.active_display_promo,
        isCollectionDisplayPromoActive,
      ],
      queryFn: async () => {
        return fetchCollectionDisplayPromoProgress(
          productBasedCollection?.token ?? "",
          IRetailerProductBasedCollectionPromoProgressRequest.build({
            promo_token: productBasedCollection?.active_display_promo!.token,
          })
        );
      },
      staleTime: Infinity,
      enabled:
        canAddToCart() &&
        isCollectionDisplayPromoActive &&
        !!productBasedCollection?.token,
    });

  const collectionOrderAmountProgress =
    promoProgressResponse?.order_amount_progress;

  const { cartItems } = useCartItems();

  const isFreeDisplayProductInCart = useMemo(() => {
    return Object.values(cartItems).some(
      (cartItem) =>
        cartItem?.product_option_token ===
        productBasedCollection?.active_display_promo?.free_display_product?.tile
          ?.selected_product_option_token
    );
  }, [
    cartItems,
    productBasedCollection?.active_display_promo?.free_display_product?.tile
      ?.selected_product_option_token,
  ]);

  const isQualifiedForCollectionDisplayPromo = useMemo(
    () =>
      MoneyMath.lessOrEqual(
        moneyOrZero(collectionOrderAmountProgress?.minimum_order_amount),
        moneyOrZero(collectionOrderAmountProgress?.current_order_amount)
      ),
    [collectionOrderAmountProgress]
  );

  const showCollectionDisplayPromoTrackingBanner = useMemo(
    () =>
      collectionOrderAmountProgress?.minimum_order_amount !== undefined &&
      collectionOrderAmountProgress?.current_order_amount !== undefined &&
      !isFreeDisplayProductInCart,
    [collectionOrderAmountProgress, isFreeDisplayProductInCart]
  );

  const formattedRemainingAmountToUnlockDisplayPromo = useMemo(() => {
    if (isQualifiedForCollectionDisplayPromo) {
      return "";
    }
    return formatMoney({
      money: MoneyMath.subtract(
        moneyOrZero(collectionOrderAmountProgress?.minimum_order_amount),
        moneyOrZero(collectionOrderAmountProgress?.current_order_amount)
      ),
      omitCentsIfZero: true,
    });
  }, [isQualifiedForCollectionDisplayPromo, collectionOrderAmountProgress]);

  return {
    isFreeDisplayProductInCart,
    isCollectionDisplayPromoActive,
    isQualifiedForCollectionDisplayPromo,
    showCollectionDisplayPromoTrackingBanner,
    formattedRemainingAmountToUnlockDisplayPromo,
    fetchDisplayPromoProgress,
    collectionOrderAmountProgress,
  };
};
